import { useCreditsSummary } from '../components/symphony/sidebar/hooks/use-credits-summary';
import { useNeedsPhoneConnect, useOpenPhoneConnect } from './phone-connect';
import { useGenericBi } from './use-generic-bi';
import { useCreditsBiFields, verifyPhoneOffer, type CreditsWidgetState, type VerifyPhoneOffer } from './use-credits-widget-state';
import { openVerifyWhatsappDialog } from './verify-whatsapp-dialog-store';

/** The BI `button_name` of the offer's CTA — the SAME string iOS and Android send (three-surface contract). */
export const VERIFY_PHONE_DAILY_LIMIT_CTA = 'verify_phone_daily_limit';

/**
 * The daily-limit wall's "Verify your phone" offer for the given widget state: the numbers to show, and
 * the action that opens the "Verify with WhatsApp" dialog over the current page (`VerifyWhatsappDialog`,
 * mounted once at the frame; contract native-apps/shared/whatsapp-phone-verify/contract.json — the same
 * sheet iOS and Android open). Its "Enter my number instead" falls back to the existing phone dialog;
 * Settings and onboarding keep that dialog as their only path. `offer` is null
 * whenever the wall should read as it always has (the decision is the pure `verifyPhoneOffer`).
 *
 * The click logs the same `pageClick` the widget's Upgrade does (`upgrade_credits` in
 * `useCreditsSummary`), with the same additional fields (`clickBiFields`, i.e. without Upgrade's
 * destination `url`), under its own `button_name`. Once the phone is verified, the dialog polls the credits
 * (or, on the number path, `useCodeVerification` re-reads them) so the raised ceiling clears the wall
 * (`credits-refresh-after-phone.ts`).
 */
export function useVerifyPhoneOffer(state: CreditsWidgetState): { offer: VerifyPhoneOffer | null; onVerify: () => void } {
  const needsPhone = useNeedsPhoneConnect();
  const openPhoneConnect = useOpenPhoneConnect();
  const { logPageClickBi } = useGenericBi();
  const creditsBiFields = useCreditsBiFields();
  const summary = useCreditsSummary();
  const offer = verifyPhoneOffer(state, needsPhone);
  return {
    offer,
    onVerify: () => {
      logPageClickBi(
        { page_name: 'menu', button_name: VERIFY_PHONE_DAILY_LIMIT_CTA, origin: 'menu' },
        // The summary is null only while the balance is unknown, when no wall renders; the bare credits
        // fields are the same base the Upgrade click builds on.
        { additionalFields: summary?.clickBiFields ?? creditsBiFields },
      );
      // No offer means no WhatsApp sheet to size; the number dialog is the safe fallback.
      if (offer) openVerifyWhatsappDialog(offer);
      else openPhoneConnect();
    },
  };
}
